import { getModuleLabelById } from "@xmiles/holopix-helper";
import { ModuleId } from "@xmiles/holopix-types";
import { EXPLORE_FAMILY_IDS, CANVAS_DRAFT_FAMILY_IDS } from "@/constants/a3ModuleIds";
import { INTELLIGENT_EDIT_V2_MODEL_ID, VISUAL_EXPANSION_V2_MODEL_ID } from "./moduleEnum";
export { canvasModuleOptions } from "./canvasModuleOptions";

export const FREE_PERSPECTIVE_MODULE_IDS = [ModuleId.FreePerspective, ModuleId.QwenFreePerspective] as const;

/**
 * ai创作资产库场景筛选选项
 * 严格约束 这里目前不应该出现自由画布的label,跟下面的canvasModuleOptions严格区分不一样
 */
export const moduleOptions = [
  { label: "全部场景", value: null, divider: true },
  // 一键成稿族：原 module 1 + A3 module 125
  { label: getModuleLabelById(ModuleId.Explore), value: [...EXPLORE_FAMILY_IDS] },
  { label: getModuleLabelById(ModuleId.Colour), value: ModuleId.Colour },
  { label: getModuleLabelById(ModuleId.Refine), value: ModuleId.Refine },
  { label: getModuleLabelById(ModuleId.Sketch), value: ModuleId.Sketch },
  { label: getModuleLabelById(ModuleId.Fission), value: ModuleId.Fission },
  { label: getModuleLabelById(ModuleId.Edit), value: ModuleId.Edit },
  // 下架：3D转换-模型 / 3D转换-图像 / 视频生成（与侧栏入口一致；历史作品标签走 getModuleLabelById 兜底）
  { label: getModuleLabelById(ModuleId.Replace), value: ModuleId.Replace },
  { label: getModuleLabelById(ModuleId.Replace), value: INTELLIGENT_EDIT_V2_MODEL_ID },
  { label: getModuleLabelById(ModuleId.Matting), value: ModuleId.Matting },
  { label: getModuleLabelById(ModuleId.Zoom), value: ModuleId.Zoom },
  { label: getModuleLabelById(ModuleId.Expansion), value: VISUAL_EXPANSION_V2_MODEL_ID },
  { label: getModuleLabelById(ModuleId.Pixelate), value: ModuleId.Pixelate },
  { label: getModuleLabelById(ModuleId.Render), value: ModuleId.Render },
  { label: getModuleLabelById(ModuleId.QwenFreePerspective), value: ModuleId.QwenFreePerspective },
  { label: getModuleLabelById(ModuleId.RepaintUpscale), value: ModuleId.RepaintUpscale },
  // AI创作 Seedance 视频模块（2.0 + Mini 合并为一个选项，避免重复）
   { label: "首尾帧视频", value: [ModuleId.AiSeedanceStartEndVideo, ModuleId.AiSeedanceMiniStartEndVideo] },
   { label: "全能参考", value: [ModuleId.AiSeedanceReferenceVideo, ModuleId.AiSeedanceMiniReferenceVideo] },
   { label: "图生视频", value: [ModuleId.AiSeedanceImage2Video, ModuleId.AiSeedanceMiniImage2Video] },
   { label: "文生视频", value: [ModuleId.AiSeedanceText2Video, ModuleId.AiSeedanceMiniText2Video] },
];

/** 版本/进行中任务展示：兼容 V2 工作流 module=50 / 51 */
export function getArtworkModuleOptionLabel(moduleId: number | null | undefined): string {
  if (moduleId == null) {
    return "";
  }
  const matched = moduleOptions.find((item) => {
    if (Array.isArray(item.value)) {
      return item.value.includes(moduleId as never);
    }
    return item.value === moduleId;
  });
  if (matched?.label) {
    return matched.label;
  }
  if (FREE_PERSPECTIVE_MODULE_IDS.includes(moduleId as (typeof FREE_PERSPECTIVE_MODULE_IDS)[number])) {
    return getModuleLabelById(ModuleId.QwenFreePerspective) as string;
  }
  if ((CANVAS_DRAFT_FAMILY_IDS as readonly number[]).includes(moduleId)) {
    return getModuleLabelById(ModuleId.CanvasDraft) as string;
  }
  return (getModuleLabelById(moduleId) as string) || String(moduleId);
}

export const sourceOptions = [
  { label: "全部来源", value: null, divider: true },
  { label: "直接生成", value: 1 },
  { label: "再迭代", value: 2 }
];

export const sortOptions = [
  { label: "最新", value: "new" },
  { label: "最热", value: "hot" }
];

// 根据 showVip 放回支持用户生图数量
import { computed } from "vue";
import { useUserStore } from "@/store/user";
import { useArtworkStore } from "@/store/artwork";

export const numberOptions = computed(() => {
  const userStore = useUserStore();
  const artworkStore = useArtworkStore();
  if (userStore.showVip) {
    return [1, 2, 4];
  } else if (artworkStore.isFlux) {
    return [1];
  }
  return [1, 2];
});

// 高清修复
export const HDOptions = [
  {
    label: "1.5x",
    value: 1.5,
    size: ""
  },
  {
    label: "2x",
    value: 2,
    size: "",
    needVip: true
  }
];

// 放大模式
export const zoomModeOptions = [
  {
    label: "写实",
    value: "person"
  },
  {
    label: "动漫",
    value: "cartoon"
  }
];

export enum RenderAspectRatios {
  Fast = "1K",
  High = "2K",
  UltraHigh = "4K"
}

/** 尺寸选项的会员要求等级 */
export type RenderModeBadge = "none" | "vip" | "svip";

export const renderModeOptionsV1 = [
  {
    label: "极速(1024)",
    value: RenderAspectRatios.Fast,
    guidKey: "renderModeFast"
  },
  {
    label: "高清(1536)",
    value: RenderAspectRatios.High,
    guidKey: "renderModeHigh"
  }
];

export const renderModeOptions: Array<{
  label: string;
  value: RenderAspectRatios;
  guidKey: string;
  badge: RenderModeBadge;
}> = [
  {
    label: "1K",
    value: RenderAspectRatios.Fast,
    guidKey: "renderModeFast",
    badge: "none"
  },
  {
    label: "2K",
    value: RenderAspectRatios.High,
    guidKey: "renderModeHigh",
    badge: "vip"
  },
  {
    label: "4K",
    value: RenderAspectRatios.UltraHigh,
    guidKey: "renderModeUltraHigh",
    badge: "svip"
  }
];

/** 根据 quality 值获取对应的显示标签（如 "1K"、"2K"、"4K"）， */
export function getRenderModeLabel(quality: string | undefined): string {
  const option = renderModeOptions.find((o) => o.value === quality);
  return option?.label ?? String(quality ?? "-");
}

import { PerspectiveType, PerspectiveRatio, PerspectiveBackground } from "@xmiles/holopix-ai";
export { PerspectiveType, PerspectiveRatio, PerspectiveBackground };

export const perspectiveTypeOptions = [
  { label: "正视图", value: PerspectiveType.Front },
  { label: "斜侧图", value: PerspectiveType.SideAngle },
  { label: "侧视图", value: PerspectiveType.Side },
  { label: "俯视", value: PerspectiveType.Top, vip: true },
  { label: "仰视", value: PerspectiveType.Bottom, vip: true },
  { label: "背视", value: PerspectiveType.Back, vip: true },
  { label: "一键三视图", value: PerspectiveType.ThreeView, svip: true },
  { label: "一键六视图", value: PerspectiveType.SixView, svip: true }
];

export const perspectiveRatioOptions = [
  { label: "原尺寸", value: PerspectiveRatio.Original },
  { label: "3:4", value: PerspectiveRatio.Portrait },
  { label: "1:1", value: PerspectiveRatio.Square },
  { label: "4:3", value: PerspectiveRatio.Landscape }
];

export const perspectiveBackgroundOptions = [
  {
    label: "空白背景",
    value: PerspectiveBackground.Remove,
    tip: "剥离复杂环境，生成纯白背景，方便后续抠图与资产提取"
  },
  {
    label: "保留背景",
    value: PerspectiveBackground.Keep,
    tip: "保留原有背景不变，只为主体生成全新角度"
  }
];


